import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppCostLabel, WhatsAppSendParams } from "../types";
import { TimelineSkeleton } from "./TimelineSkeleton";
import { WhatsAppSendError } from "./whatsappSendError";

const costKey = (label: WhatsAppCostLabel) =>
  label === "Gratis"
    ? "crm.whatsapp.conversation.composer.cost_free"
    : "crm.whatsapp.conversation.composer.cost_paid";

/**
 * Almikia Epic EW2 (TASK-079): the WhatsApp composer. While the 24 h clock is open it is a plain
 * free-text box; when it is closed the text is blocked and a template selector appears (only
 * when the server says templates are enabled), each template labelled 'Gratis' or 'Con costo'
 * from the server cost labels. A 'Con costo' template asks for confirmation (and sends
 * `confirm_cost`) before it goes out. No advisor signature is ever appended. The send itself
 * (and every window / cost decision) is the server's: this component only reflects it.
 */
export const WhatsAppComposer = ({
  conversationId,
  freeTextOpen,
  costKeyForCatalogue,
  onSent,
}: {
  conversationId: Identifier;
  /** Whether the rendered 24 h clock still has time left. */
  freeTextOpen: boolean;
  /** Changes when the server cost label of a template changes, to refresh the catalogue. */
  costKeyForCatalogue: string;
  onSent: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [text, setText] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const catalogue = useQuery({
    queryKey: [
      "whatsapp",
      "templates",
      String(conversationId),
      costKeyForCatalogue,
    ],
    queryFn: () => dataProvider.getWhatsAppTemplates(conversationId),
    enabled: !freeTextOpen,
  });
  const template = catalogue.data?.templates.find(
    (row) => String(row.id) === templateId,
  );

  const send = useMutation({
    mutationFn: (params: WhatsAppSendParams) =>
      dataProvider.sendWhatsAppMessage(params),
    onSuccess: () => {
      setText("");
      setTemplateId("");
      setVariables({});
      setConfirmOpen(false);
      notify("crm.whatsapp.conversation.composer.sent_notice", {
        type: "info",
      });
      onSent();
    },
    onError: (error) => {
      setConfirmOpen(false);
      notify(
        error instanceof WhatsAppSendError
          ? error.message
          : "crm.whatsapp.conversation.composer.send_error",
        { type: "error" },
      );
    },
  });

  const sendTemplate = (confirmCost: boolean) => {
    if (!template) return;
    send.mutate({
      conversationId,
      type: "template",
      templateId: template.id,
      variables: template.variables.map((name) => variables[name] ?? ""),
      confirmCost,
    });
  };

  if (freeTextOpen) {
    return (
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!text.trim()) return;
          send.mutate({ conversationId, type: "text", text: text.trim() });
        }}
      >
        <Textarea
          aria-label={translate(
            "crm.whatsapp.conversation.composer.text_label",
          )}
          placeholder={translate(
            "crm.whatsapp.conversation.composer.placeholder",
          )}
          value={text}
          maxLength={4096}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          type="submit"
          className="self-end"
          disabled={!text.trim() || send.isPending}
        >
          {translate("crm.whatsapp.conversation.composer.send")}
        </Button>
      </form>
    );
  }

  const variablesFilled =
    !!template &&
    template.variables.every((name) => (variables[name] ?? "").trim() !== "");

  return (
    <div className="flex flex-col gap-3">
      <Textarea
        aria-label={translate("crm.whatsapp.conversation.composer.text_label")}
        value=""
        disabled
        readOnly
      />
      <p className="text-sm text-muted-foreground">
        {translate("crm.whatsapp.conversation.composer.closed_notice")}
      </p>
      {catalogue.isPending ? <TimelineSkeleton rows={1} /> : null}
      {catalogue.data && !catalogue.data.templates_enabled ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.whatsapp.conversation.composer.templates_off")}
        </p>
      ) : null}
      {catalogue.data?.templates_enabled ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span aria-hidden="true">
              {translate("crm.whatsapp.conversation.composer.template_label")}
            </span>
            <select
              aria-label={translate(
                "crm.whatsapp.conversation.composer.template_label",
              )}
              className="h-11 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              value={templateId}
              onChange={(event) => {
                setTemplateId(event.target.value);
                setVariables({});
              }}
            >
              <option value="">
                {translate(
                  "crm.whatsapp.conversation.composer.template_placeholder",
                )}
              </option>
              {catalogue.data.templates.map((row) => (
                <option key={String(row.id)} value={String(row.id)}>
                  {`${row.label} · ${translate(costKey(row.cost_label))}`}
                </option>
              ))}
            </select>
          </div>
          {template?.variables.map((name) => (
            <Input
              key={name}
              aria-label={translate(
                "crm.whatsapp.conversation.composer.variable_label",
                { name },
              )}
              placeholder={name}
              maxLength={1024}
              value={variables[name] ?? ""}
              onChange={(event) =>
                setVariables((current) => ({
                  ...current,
                  [name]: event.target.value,
                }))
              }
            />
          ))}
          <Button
            type="button"
            className="self-end"
            disabled={!template || !variablesFilled || send.isPending}
            onClick={() =>
              template?.cost_label === "Con costo"
                ? setConfirmOpen(true)
                : sendTemplate(false)
            }
          >
            {translate("crm.whatsapp.conversation.composer.send")}
          </Button>
        </div>
      ) : null}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("crm.whatsapp.conversation.composer.confirm_title")}
            </DialogTitle>
            <DialogDescription>
              {translate("crm.whatsapp.conversation.composer.confirm_body", {
                name: template?.label ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {translate("crm.whatsapp.conversation.composer.cancel")}
            </Button>
            <Button
              disabled={send.isPending}
              onClick={() => sendTemplate(true)}
            >
              {translate("crm.whatsapp.conversation.composer.confirm_send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
